// Pure helper: maps UI filter state -> API params for GET /v1/coupon/list_coupons
import { cleanObject } from "../../../helpers";
import {
  ListCouponsQuery,
  CouponType,
  CouponStatus,
  CouponDiscountType,
} from "../../../redux/features/types/couponListTypes";
import { DateRangeType } from "../../ui/DateRangeSelector";

export interface CouponListFilters {
  page: number;
  limit: number;
  search?: string;
  buyerName?: string;
  couponType?: CouponType | null;
  status?: CouponStatus | null;
  discountType?: CouponDiscountType | null;
  createdRange?: DateRangeType;
  expiryRange?: DateRangeType;
}

const toIso = (value: unknown): string | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) {
    // Guard against partially-typed dates from the picker (Invalid Date has NaN time).
    const time = value.getTime();
    if (Number.isNaN(time)) return undefined;
    return value.toISOString();
  }
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : value;
  }
  return undefined;
};

/**
 * Maps UI filter state to API-ready query params for GET /v1/coupon/list_coupons.
 * Drops null / undefined / empty values so we never send `?couponType=` to the API.
 * `page` is converted from 0-based UI index to 1-based API index here.
 */
export const buildListCouponQuery = (
  filters: CouponListFilters
): ListCouponsQuery => {
  const {
    page,
    limit,
    search,
    buyerName,
    couponType,
    status,
    discountType,
    createdRange,
    expiryRange,
  } = filters;

  return cleanObject({
    page: page + 1,
    limit,
    search: search?.trim() || undefined,
    buyerName: buyerName?.trim() || undefined,
    couponType: couponType ?? undefined,
    status: status ?? undefined,
    discountType: discountType ?? undefined,
    createdFrom: toIso(createdRange?.startDate),
    createdTo: toIso(createdRange?.endDate),
    expiryFrom: toIso(expiryRange?.startDate),
    expiryTo: toIso(expiryRange?.endDate),
  }) as ListCouponsQuery;
};
